import React, { useState, useEffect, useRef } from 'react';
import { fileService } from '../services/api';

const LazyImage = ({ 
  fileName, 
  alt, 
  className = "",
  width = 200,
  height = 150,
  quality = 50,
  onError,
  onLoad,
  fallbackIcon 
}) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [inView, setInView] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Intersection Observer para lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
      observerRef.current = observer;
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Cargar imagen cuando esté en vista
  useEffect(() => {
    if (inView && fileName && !imageUrl && !loading && !error) {
      setLoading(true);
      // Usar thumbnail para imágenes
      fileService.getThumbnail(fileName, width, height, quality)
        .then(response => {
          setImageUrl(response.url);
          if (onLoad) onLoad();
        })
        .catch(err => {
          setError(true);
          console.error('Error cargando imagen:', err);
          
          // Si el error indica que el archivo no existe, mostrar mensaje específico
          if (err.message && (err.message.includes('no existe') || err.message.includes('eliminado'))) {
            console.warn('Archivo no encontrado, puede haber sido eliminado:', fileName);
          }
          
          if (onError) onError(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [inView, fileName, width, height, quality, imageUrl, loading, error, onLoad, onError]);

  if (error) {
    return (
      <div ref={imgRef} className={`${className} flex items-center justify-center bg-gray-800 rounded-lg border-2 border-gray-600`}>
        {fallbackIcon || (
          <div className="text-red-500 text-center">
            <div className="text-2xl">⚠️</div>
            <div className="text-xs mt-1">Archivo no encontrado</div>
            <div className="text-xs text-gray-400 mt-1">Puede haber sido eliminado</div>
          </div>
        )}
      </div>
    );
  }

  if (loading || !inView) {
    return (
      <div ref={imgRef} className={`${className} flex items-center justify-center bg-gray-800 rounded-lg border-2 border-gray-600`}>
        {loading ? (
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
        ) : (
          <div className="text-gray-500 text-center">
            <div className="text-2xl">🖼️</div>
            <div className="text-xs mt-1">Imagen</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={imgRef} className={`${className} rounded-lg overflow-hidden`}>
      <img 
        src={imageUrl} 
        alt={alt || fileName}
        className="w-full h-full object-cover"
        onError={() => {
          setError(true);
          if (onError) onError(new Error('Error loading image'));
        }}
        onLoad={() => {
          if (onLoad) onLoad();
        }}
        loading="lazy"
      />
    </div>
  );
};

export default LazyImage; 